import { useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { clearAccessToken, setAccessToken } from '@/lib/authToken';

export const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkAuth } = useAuth();

  const handleToken = useCallback(async (token: string) => {
    try {
      // Phase 2 auth-hardening: access token is in URL fragment, stored
      // in memory. The refresh cookie was set by the backend on the same
      // redirect.
      setAccessToken(token);
      const user = await checkAuth();

      if (user && user.role === 'admin') {
        navigate('/', { replace: true });
      } else {
        clearAccessToken();
        navigate('/sign-in', {
          replace: true,
          state: { error: 'Access denied. Admin privileges required.' },
        });
      }
    } catch {
      clearAccessToken();
      navigate('/sign-in', {
        replace: true,
        state: { error: 'Sign in failed. Please try again.' },
      });
    }
  }, [checkAuth, navigate]);

  const hashParam = (name: string): string | null => {
    const raw = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;
    return new URLSearchParams(raw).get(name);
  };

  useEffect(() => {
    const token = hashParam('token') ?? searchParams.get('token');
    const mfaChallenge = hashParam('mfa');
    const error = searchParams.get('error');

    if (error) {
      navigate('/sign-in', {
        replace: true,
        state: { error: decodeURIComponent(error) },
      });
    } else if (mfaChallenge) {
      // Phase 4 — Google flow proved identity but the admin account also
      // requires a TOTP code. Hand the challenge token to /sign-in/mfa.
      navigate('/sign-in/mfa', {
        replace: true,
        state: { challengeToken: mfaChallenge },
      });
    } else if (token) {
      handleToken(token);
    } else {
      navigate('/sign-in', { replace: true });
    }
  }, [searchParams, navigate, handleToken]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto" />
        <p className="mt-3 text-sm text-gray-500">Completing sign in...</p>
      </div>
    </div>
  );
};
